import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { showSnackbar } from './Snackbar.jsx';
import './Login.css';

const Login = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showRetryMessage, setShowRetryMessage] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    
    const { login } = useAuth();
    
    // Check if modal was opened due to authentication failure
    useEffect(() => {
        if (isOpen) {
            // Check if there are pending requests (indicates auth failure)
            const checkForPendingRequests = () => {
                // We'll use a custom event to check this
                const event = new CustomEvent('checkPendingRequests');
                window.dispatchEvent(event);
            };
            
            // Small delay to ensure auth service is ready
            setTimeout(checkForPendingRequests, 100);
        }
    }, [isOpen]);
    
    // Listen for pending requests found event
    useEffect(() => {
        const handlePendingRequestsFound = (event) => {
            setShowRetryMessage(true);
        };
        
        window.addEventListener('pendingRequestsFound', handlePendingRequestsFound);
        
        return () => {
            window.removeEventListener('pendingRequestsFound', handlePendingRequestsFound);
        };
    }, []);
    
    // Make the login modal globally available
    useEffect(() => {
        window.openLoginModal = () => {
            console.log('Opening login modal');
            setIsOpen(true);
        };
        
        return () => {
            delete window.openLoginModal;
        };
    }, []);
    
    // Listen for authentication required events
    useEffect(() => {
        const handleAuthenticationRequired = () => {
            console.log('Authentication required, opening login modal');
            setIsOpen(true);
        };
        
        window.addEventListener('authenticationRequired', handleAuthenticationRequired);
        
        return () => {
            window.removeEventListener('authenticationRequired', handleAuthenticationRequired);
        };
    }, []);
    
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when user starts typing
        if (error) {
            setError('');
        }
    };
    
    const validateForm = () => {
        if (!formData.username.trim() || !formData.password.trim()) {
            setError('Wypełnij wszystkie wymagane pola');
            return false;
        }
        return true;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        setError(''); // Clear any previous errors
        
        try {
            await login(formData.username, formData.password);
            showSnackbar('Logowanie udane!');
            handleClose();
        } catch (error) {
            // Show error within the modal instead of global snackbar
            setError(error.message || 'Logowanie nie powiodło się. Sprawdź swoje dane i spróbuj ponownie.');
        } finally {
            setLoading(false);
        }
    };
    
    const handleClose = () => {
        if (!loading) {
            setIsOpen(false);
            setShowRetryMessage(false);
            setError(''); // Clear error when closing
            // Reset form data
            setFormData({
                username: '',
                password: ''
            });
            // Ensure page scrolls to top when modal is closed with instant behavior
            window.scrollTo({ top: 0, behavior: 'instant' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }
    };
    
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !loading) {
            handleClose();
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <div className="login-modal-overlay" onClick={handleBackdropClick}>
            <div className="login-modal">
                <div className="login-modal-header">
                    <h2>Witaj ponownie</h2>
                    <button 
                        className="close-btn" 
                        onClick={handleClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>
                
                <div className="login-modal-content">
                    {showRetryMessage && (
                        <div className="retry-message">
                            <p>Twoja sesja wygasła. Zaloguj się ponownie, aby kontynuować poprzednią akcję.</p>
                        </div>
                    )}
                    
                    <p className="login-subtitle">Zaloguj się do swojego konta</p>
                    
                    {error && (
                        <div className="error-container">
                            <div className="error-message">
                                <p>{error}</p>
                            </div>
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="username">Nazwa użytkownika</label>
                            <input
                                type="text"
                                id="username"
                                value={formData.username}
                                onChange={(e) => handleInputChange('username', e.target.value)}
                                placeholder="Wprowadź nazwę użytkownika"
                                autoCapitalize="none"
                                required
                                disabled={loading}
                                className={error ? 'error' : ''}
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="password">Hasło</label>
                            <input
                                type="password"
                                id="password"
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                placeholder="Wprowadź hasło"
                                required
                                disabled={loading}
                                className={error ? 'error' : ''}
                            />
                        </div>
                        
                        <button 
                            type="submit" 
                            className="submit-btn" 
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Logowanie...
                                </>
                            ) : (
                                'Zaloguj się'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;
